import * as DialogPrimitive from "@radix-ui/react-dialog";
import type { ReactNode } from "react";
import { useCallback, useId, useRef } from "react";

import { cn } from "../lib/cn";
import { FOCUS_RING, REDUCED_MOTION_SAFE } from "../lib/focus";
import { OVERLAY_FOOTER_LAYOUT } from "../lib/overlayFooter";

export type typeDialogProps = {
  /**
   * On a route screen this is derived from a query param
   * (`?dialog=create-pool`), not from component state, so browser-back closes
   * the overlay and a refresh mid-flow reopens exactly where the fan was.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Required. Every overlay is announced by name. */
  title: string;
  /** One supporting sentence, read after the title. */
  description?: string | undefined;
  children: ReactNode;
  /**
   * Action row. Pass the buttons in READING ORDER — the non-destructive one
   * ("Cancel," or the safer alternative that replaces it) first, the primary or
   * destructive one last. That is also the correct tab order.
   *
   * The layout then places them per the platform rule, and the caller never
   * decides: under 768px they stack full width with the primary/destructive on
   * TOP; at 768px and up they sit in one row with it on the RIGHT.
   */
  footer?: ReactNode | undefined;
  /**
   * Focus the first editable field on open so a fan can
   * start typing without an extra tap. Set false for a read-only overlay with
   * nothing to type into.
   */
  autoFocus?: boolean | undefined;
  /**
   * When true, Escape and a scrim click do NOT
   * close. They route to `onRequestClose` instead, so the discard confirm can
   * happen before anything typed is thrown away. The X does the same.
   */
  isDirty?: boolean | undefined;
  /**
   * Called instead of closing whenever `isDirty` is true. Show the discard
   * confirm here; call `onOpenChange(false)` yourself once the fan chooses to
   * discard.
   */
  onRequestClose?: (() => void) | undefined;
  /** Hides the X. The overlay then has to be closable from its own footer. */
  hideCloseButton?: boolean | undefined;
  className?: string | undefined;
};

const CloseIcon = () => (
  <svg
    className="h-icon-md w-icon-md"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m5 5 10 10M15 5 5 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * ISSUE-1096. Vertical room inside the scroll body for a compact control's
 * hit-slop.
 *
 * A `size="sm"` Button is 36px tall (`controlHeightSm`) and `HIT_SLOP`
 * (`../lib/focus`) pads it to the 44px floor (`touchTarget`) with an
 * absolutely positioned `::after` that reaches 4px past its top and bottom
 * edges. When such a button is the last thing in the body (the "Copy share
 * link" button on "Your pool is ready"), that 4px is scrollable overflow, and
 * `overflow-y-auto` paints a scrollbar on a dialog whose content fits. The
 * same 4px was also clipped out of the tap target.
 *
 * `py-1` is that 4px, (44 - 36) / 2. It is taken out of the header's bottom
 * padding and the footer's top padding, so every gap a fan sees stays 16px and
 * nothing in a dialog with a footer moves. A dialog without a footer ends 4px
 * lower. `HIT_SLOP` itself says its parent must not clip overflow; a scroll
 * body has to clip, so it makes room instead.
 *
 * The real-browser proof is `tests/e2e/src/app/dialogScrollBody.e2e.ts`.
 */
const SCROLL_BODY_HIT_SLOP_ROOM = "py-1";

/**
 * The platform's transient overlay: a bottom sheet under 768px, a centered
 * dialog at 768px and up, from ONE component and one set of props.
 *
 * A screen never branches its own JSX on viewport width to pick between a
 * drawer and a dialog — that decision lives here and only here, which is also
 * what keeps focus handling, the dirty-state guard, and autofocus from drifting
 * apart between the two shapes.
 *
 * Radix owns the hard parts: focus trap, focus restore to the trigger on close,
 * `aria-modal`, scroll lock, and inert background. Never hand-roll those.
 */
export const Dialog = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  autoFocus = true,
  isDirty = false,
  onRequestClose,
  hideCloseButton = false,
  className,
}: typeDialogProps): ReactNode => {
  const generatedId = useId();
  const titleId = `${generatedId}-title`;
  const contentRef = useRef<HTMLDivElement>(null);

  /**
   * One close path for the X, Escape, and the scrim. Routing all three through
   * here is what makes "cancel always means nothing happened" a property of the
   * component rather than a thing each screen has to remember.
   */
  const requestClose = useCallback(() => {
    if (isDirty) {
      onRequestClose?.();
      return;
    }
    onOpenChange(false);
  }, [isDirty, onOpenChange, onRequestClose]);

  const handleOpenAutoFocus = useCallback(
    (event: Event) => {
      if (!autoFocus) return;
      const content = contentRef.current;
      if (!content) return;
      const firstField = content.querySelector<HTMLElement>(
        "input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled])",
      );
      // No field means nothing to type into — let Radix focus the content
      // itself, which is correct for a read-only picker.
      if (!firstField) return;
      event.preventDefault();
      firstField.focus();
    },
    [autoFocus],
  );

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (next) {
          onOpenChange(true);
          return;
        }
        requestClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          // The scrim is a close path in its own right, and the one a thumb
          // brushes by accident. Naming it is what lets a screen's own tests
          // drive that path instead of standing in for it with Escape.
          data-testid="dialog-scrim"
          className={cn(
            "fixed inset-0 z-[var(--sm-z-scrim)] bg-scrim",
            "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
            REDUCED_MOTION_SAFE,
          )}
        />
        <DialogPrimitive.Content
          ref={contentRef}
          aria-labelledby={titleId}
          // No aria-describedby here: Radix ties the content to the
          // Description below by itself once one renders, and appends its own
          // id to anything passed in. An id of ours would be listed next to
          // an id no element carries.
          onOpenAutoFocus={handleOpenAutoFocus}
          data-dirty={isDirty ? "true" : undefined}
          className={cn(
            "fixed z-[var(--sm-z-dialog)] flex flex-col bg-surface-elevated shadow-lg",
            REDUCED_MOTION_SAFE,
            // Under 768px: a bottom sheet pinned to the bottom edge, rounded on
            // top, height-capped so the page behind stays visible.
            "inset-x-0 bottom-0 max-h-[85dvh] rounded-t-lg",
            "pb-[calc(1rem+env(safe-area-inset-bottom))]",
            "data-[state=open]:animate-sheet-in data-[state=closed]:animate-sheet-out",
            // 768px and up: a centered dialog. A bottom sheet stretched across
            // a desktop window reads as an unfinished port.
            "md:inset-x-auto md:bottom-auto md:top-1/2 md:left-1/2 md:w-full md:max-w-[560px]",
            "md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-md md:pb-0",
            "md:data-[state=open]:animate-dialog-in md:data-[state=closed]:animate-dialog-out",
            className,
          )}
        >
          {/* `pb-3`, not `pb-4`: the scroll body's `py-1` supplies the last
              4px, so the gap to the content is still 16px. See
              SCROLL_BODY_HIT_SLOP_ROOM. */}
          <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3">
            <div className="flex flex-col gap-1">
              <DialogPrimitive.Title id={titleId} className="text-h2 text-ink">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="text-body text-ink-secondary">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            {hideCloseButton ? null : (
              <button
                type="button"
                onClick={requestClose}
                aria-label="Dismiss"
                className={cn(
                  "-m-2 flex h-touch-target w-touch-target shrink-0 items-center justify-center",
                  "rounded-sm text-ink-secondary hover:bg-surface-sunken hover:text-ink",
                  FOCUS_RING,
                )}
              >
                <CloseIcon />
              </button>
            )}
          </div>

          <div
            data-testid="dialog-body"
            className={cn(
              "flex-1 overflow-y-auto px-4",
              SCROLL_BODY_HIT_SLOP_ROOM,
            )}
          >
            {children}
          </div>

          {footer ? (
            // Fixed footer, outside the scroll body, so the primary CTA is
            // never scrolled below the fold on a phone.
            <div
              data-testid="dialog-footer"
              // `pt-3`, not `pt-4`, for the same reason as the header's
              // `pb-3`: the scroll body's `py-1` is the other 4px.
              className={cn(OVERLAY_FOOTER_LAYOUT, "px-4 pt-3 pb-4")}
            >
              {footer}
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
